import React, { useEffect, useState } from 'react';
import { getAuthToken } from '../../auth/context/AuthContext';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/admin`;

// The statement stays in the page. Turn this on to show it again.
const SHOW_WALLET_STATEMENT = false;

function whenLabel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Company wallet. Shown only to a company admin. Every admin of the same
 * company loads this same balance and statement.
 */
function Party({ title, party }) {
  const lines = [
    party?.name,
    ...(party?.lines || []),
    party?.gstin ? `GSTIN: ${party.gstin}` : '',
    party?.email,
    party?.phone,
    party?.attention ? `Attn: ${party.attention}` : '',
  ].filter(Boolean);
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color: '#5c6570', marginBottom: 6 }}>{title}</div>
      {lines.map((line, i) => (
        <div key={line} style={{ fontSize: 12.5, fontWeight: i === 0 ? 700 : 400, color: '#1c2430', lineHeight: 1.45 }}>{line}</div>
      ))}
    </div>
  );
}

export function InvoiceSheet({ invoice }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e4e7ee', borderRadius: 10, overflow: 'hidden', color: '#1c2430' }}>
      <div style={{ background: '#12203f', color: '#fff', padding: '16px 18px', display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{invoice.from?.name}</div>
          <div style={{ fontSize: 11, letterSpacing: 1.4, opacity: 0.8, marginTop: 2 }}>INVOICE</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 12 }}>
          <div style={{ fontWeight: 700 }}>{invoice.number}</div>
          <div style={{ opacity: 0.85, marginTop: 2 }}>{invoice.date}</div>
          <div style={{ marginTop: 6, fontWeight: 700 }}>{invoice.status}</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 32, padding: '16px 18px' }}>
        <Party title="BILL FROM" party={invoice.from} />
        <Party title="BILL TO" party={invoice.bill_to} />
      </div>

      <div style={{ padding: '0 18px 16px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: '#eef1f6' }}>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11 }}>Description</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', fontSize: 11 }}>Qty</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', fontSize: 11 }}>Rate</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', fontSize: 11 }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lines || []).map((line) => (
              <tr key={line.description} style={{ borderBottom: '1px solid #e4e7ee' }}>
                <td style={{ padding: '10px' }}>{line.description}</td>
                <td style={{ padding: '10px', textAlign: 'right' }}>{line.qty}</td>
                <td style={{ padding: '10px', textAlign: 'right' }}>{line.rate}</td>
                <td style={{ padding: '10px', textAlign: 'right', fontWeight: 700 }}>{line.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ width: '100%', marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 6 }}>
            <span>Subtotal</span><span>{invoice.subtotal}</span>
          </div>
          {(invoice.taxes || []).map((tax) => (
            <div key={tax.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 6 }}>
              <span>{tax.label}</span><span>{tax.amount}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', background: '#12203f', color: '#fff', padding: '8px 10px', borderRadius: 6, fontWeight: 700 }}>
            <span>Total paid</span><span>{invoice.total}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function loadCheckout() {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Razorpay'));
    document.body.appendChild(script);
  });
}

function moneyLabel(currency, cents) {
  const body = (Math.max(0, cents) / 100).toFixed(2);
  return currency === 'INR' ? `₹${body}` : `$${body}`;
}

function payableNote(custom, wallet) {
  if (!wallet || wallet.billing_blocked || wallet.currency !== 'INR') return '';
  const text = String(custom || '').trim();
  if (!text) return '';
  const major = Number(text);
  if (!Number.isFinite(major) || major <= 0) return '';
  const cents = Math.round(major * 100);
  const gst = Number(wallet.gst_percent) || 0;
  const gstCents = gst ? Math.floor((cents * gst + 50) / 100) : 0;
  const payable = moneyLabel(wallet.currency, cents + gstCents);
  const credit = moneyLabel(wallet.currency, cents);
  if (!gst) return `Payable ${payable}. No GST. ${credit} is added to the wallet.`;
  return `Payable ${payable}, including ${wallet.gst_label || '18% GST'}. ${credit} is added to the wallet.`;
}

export default function WalletCard({ onOrdersChange }) {
  const [wallet, setWallet] = useState(null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [custom, setCustom] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [olderBusy, setOlderBusy] = useState(false);

  const load = async (cursor) => {
    const params = new URLSearchParams();
    if (cursor?.before && cursor?.before_id) {
      params.set('before', cursor.before);
      params.set('before_id', cursor.before_id);
    }
    const query = params.toString();
    const res = await fetch(`${API}/wallet${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not load wallet');
    if (cursor) {
      setWallet((prev) => ({
        ...data,
        transactions: [...(prev?.transactions || []), ...(data.transactions || [])],
      }));
    } else {
      setWallet(data);
    }
    return data;
  };

  const loadOlder = async () => {
    if (!wallet?.transactions_next || olderBusy) return;
    setOlderBusy(true);
    try {
      await load(wallet.transactions_next);
    } catch (e) {
      setDetailErr(e.message);
    } finally {
      setOlderBusy(false);
    }
  };

  useEffect(() => {
    let gone = false;
    load().catch((e) => { if (!gone) setErr(e.message); });
    return () => { gone = true; };
  }, []);

  const pay = async (amountMinor) => {
    setBusy(true); setErr(''); setNote('');
    try {
      const res = await fetch(`${API}/wallet/recharge`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount_minor: amountMinor }),
      });
      const order = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof order.detail === 'string' ? order.detail : 'Could not start payment');
      onOrdersChange?.();
      await loadCheckout();
      const before = wallet?.balance_minor;
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.checkout_amount,
        currency: order.currency,
        order_id: order.order_id,
        name: 'Amartam Technology Solutions Pvt Ltd.',
        description: order.description || 'Wallet recharge',
        modal: {
          ondismiss: async () => {
            try {
              const res = await fetch(`${API}/wallet/recharge/cancel`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${getAuthToken()}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ order_id: order.order_id }),
              });
              const data = await res.json().catch(() => ({}));
              onOrdersChange?.();
              if (data.status === 'canceled') setNote('Payment canceled.');
            } catch (_) {
              onOrdersChange?.();
            }
          },
        },
        handler: async (response) => {
          try {
            const confirm = await fetch(`${API}/wallet/recharge/confirm`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                order_id: response.razorpay_order_id,
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature,
              }),
            });
            const data = await confirm.json().catch(() => ({}));
            if (!confirm.ok) {
              throw new Error(typeof data.detail === 'string' ? data.detail : 'Payment could not be confirmed');
            }
            if (data.credited || !data.webhook_required) {
              await load();
              onOrdersChange?.();
              setNote(data.credited ? 'Payment added to the wallet.' : 'This payment was already added.');
              return;
            }
          } catch (e) {
            setErr(e.message);
            return;
          }
          setNote('Payment sent. The balance updates when the bank confirms it — not from this screen alone.');
          let tries = 0;
          const timer = setInterval(async () => {
            tries += 1;
            try {
              const next = await load();
              if (next.balance_minor !== before) onOrdersChange?.();
              if (next.balance_minor !== before || tries >= 6) clearInterval(timer);
            } catch (_) {
              if (tries >= 6) clearInterval(timer);
            }
          }, 2000);
        },
      });
      rzp.on('payment.failed', async () => {
        try {
          const res = await fetch(`${API}/wallet/recharge/fail`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ order_id: order.order_id }),
          });
          const data = await res.json().catch(() => ({}));
          onOrdersChange?.();
          if (data.status === 'failed') setErr('Payment failed.');
        } catch (_) {
          onOrdersChange?.();
        }
      });
      rzp.open();
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  };

  const openDetail = async (id) => {
    setDetailErr('');
    setDetail({ loading: true, id });
    try {
      const res = await fetch(`${API}/wallet/transactions/${id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not load this transaction');
      setDetail(data);
    } catch (e) {
      setDetail(null);
      setDetailErr(e.message);
    }
  };

  const downloadInvoice = async (row) => {
    setDownloading(true);
    setDetailErr('');
    try {
      const res = await fetch(`${API}/wallet/transactions/${row.id}/invoice`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not download the invoice');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fromHeader = (res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/);
      const fromNumber = (row?.invoice?.number || '').replace(/^#/, '').replace(/[^A-Za-z0-9_-]/g, '');
      link.href = url;
      link.download = fromNumber ? `${fromNumber}.pdf` : ((fromHeader && fromHeader[1]) || 'invoice.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDetailErr(e.message);
    }
    setDownloading(false);
  };

  const payCustom = () => {
    const major = Math.round(Number(custom) * 100) / 100;
    if (!Number.isFinite(major) || major <= 0) {
      setErr('Enter an amount');
      return;
    }
    pay(Math.round(major * 10000));
  };

  return (
    <div className="card">
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
        Balance
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
        Shared by every admin of this company. Recruiters do not see this.
      </div>

      {err && <div className="alert alert-error">{err}</div>}
      {!wallet && !err && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>}

      {wallet && (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)' }}>
              {wallet.balance_display}
            </div>
            {wallet.frozen && (
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--danger, #b91c1c)' }}>Frozen</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
            {wallet.currency === 'INR' ? 'Indian rupees' : 'US dollars'}
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Add money</div>
            {!wallet.razorpay_ready ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Razorpay is not set up yet, so recharge is unavailable.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  {(wallet.packs || []).map((label, i) => (
                    <button
                      key={label}
                      type="button"
                      className="btn btn-sm"
                      disabled={busy || wallet.frozen || wallet.billing_blocked}
                      onClick={() => pay(wallet.packs_minor[i])}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="form-input"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder={wallet.currency === 'INR' ? 'Custom rupees' : 'Custom dollars'}
                    style={{ maxWidth: 180 }}
                  />
                  <button type="button" className="btn btn-primary btn-sm" disabled={busy || wallet.frozen || wallet.billing_blocked} onClick={payCustom}>
                    {busy ? 'Opening…' : 'Pay'}
                  </button>
                </div>
                {payableNote(custom, wallet) && (
                  <div style={{ fontSize: 12, marginTop: 6, color: 'var(--text-primary)' }}>
                    {payableNote(custom, wallet)}
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  Minimum {wallet.minimum_display}. The balance changes only after the bank confirms.
                </div>
                {wallet.billing_note && (
                  <div style={{ fontSize: 12, marginTop: 6, color: wallet.billing_blocked ? 'var(--danger, #b91c1c)' : 'var(--text-muted)' }}>
                    {wallet.billing_note}
                  </div>
                )}
              </>
            )}
            {note && <div style={{ fontSize: 13, marginTop: 8 }}>{note}</div>}
          </div>

          {SHOW_WALLET_STATEMENT ? (wallet.transactions.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No transactions yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>What</th>
                    <th>Who</th>
                    <th>Candidate</th>
                    <th>Amount</th>
                    <th>Balance</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {wallet.transactions.map(row => (
                    <tr key={row.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{whenLabel(row.when)}</td>
                      <td>{row.direction === 'in' ? 'Added' : 'Used'}</td>
                      <td>{row.label}</td>
                      <td>{row.actor_name || '—'}</td>
                      <td>{row.candidate_name || '—'}</td>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.amount_display}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{row.balance_after_display}</td>
                      <td>
                        <button type="button" className="btn btn-sm" onClick={() => openDetail(row.id)}>
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {wallet.transactions_has_more && (
                <button type="button" className="btn btn-sm" disabled={olderBusy} onClick={loadOlder} style={{ marginTop: 12 }}>
                  {olderBusy ? 'Loading…' : 'Older'}
                </button>
              )}
            </div>
          )) : null}
          {SHOW_WALLET_STATEMENT && detailErr && <div className="alert alert-error" style={{ marginTop: 12 }}>{detailErr}</div>}
        </>
      )}

      {detail && (
        <div className="modal-backdrop" onClick={() => setDetail(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
            {detail.loading ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
            ) : (
              <>
                {detail.invoice ? (
                  <InvoiceSheet invoice={detail.invoice} />
                ) : (
                  <>
                    <h3>Transaction</h3>
                    <div className="modal-sub">{whenLabel(detail.when)}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px 12px', fontSize: 13, marginTop: 16 }}>
                      <div style={{ color: 'var(--text-muted)' }}>Type</div>
                      <div>{detail.direction === 'in' ? 'Added' : 'Used'}</div>
                      <div style={{ color: 'var(--text-muted)' }}>What</div>
                      <div>{detail.label}</div>
                      <div style={{ color: 'var(--text-muted)' }}>Who</div>
                      <div>{detail.actor_name || '—'}</div>
                      <div style={{ color: 'var(--text-muted)' }}>Candidate</div>
                      <div>{detail.candidate_name || '—'}</div>
                      <div style={{ color: 'var(--text-muted)' }}>Amount</div>
                      <div style={{ fontWeight: 700 }}>{detail.amount_display}</div>
                      <div style={{ color: 'var(--text-muted)' }}>Balance after</div>
                      <div>{detail.balance_after_display}</div>
                    </div>
                  </>
                )}

                <div className="modal-actions">
                  {detail.has_invoice && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={downloading}
                      onClick={() => downloadInvoice(detail)}
                    >
                      {downloading ? 'Downloading…' : 'Download invoice'}
                    </button>
                  )}
                  <button type="button" className="btn" onClick={() => setDetail(null)}>Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
